// =============================================================================
// currency.js, Multi-currency display helpers (Phase 38)
// =============================================================================
//
// Three layers of truth, per PRODUCT_REVIEW.md:
//   • Native     , what's stored on the row (per account / per transaction).
//                   ALWAYS show as native. Never silently convert.
//   • Display    , optional viewer-side override (`"show me everything in AED"`).
//                   Defaults to the user's base currency.
//   • Base       , the headline currency in `profiles.base_currency`. Used
//                   for Net Worth, FI projections, monthly surplus, Clarity.
//
// This file is the SINGLE place we format currency in the UI. Anywhere a
// monetary value is rendered, it should pass through `formatMoney(...)` or
// one of its specialised helpers below.
//
// Conventions:
//   • Minus sign BEFORE the currency symbol: `-₹1,234.56`, NOT `₹-1,234.56`.
//   • INR formatting uses lakhs/crores grouping (Intl `en-IN` locale).
//   • Other currencies use the standard ISO `Intl.NumberFormat`.
//   • Headline numbers (Net Worth, Surplus, Income) get auto-compaction
//     (`₹1.03 cr`) above a threshold so they fit one line on mobile.
//   • The "headline parenthesis" pattern: `AED 1,200 · ≈ ₹27,200`.
// =============================================================================
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Currencies that group as lakhs/crores. Add MUR/BDT/NPR if we ever support
// them, same numbering system.
const INDIAN_GROUPING = new Set(['INR']);

/**
 * Native locale per currency. We pick a locale that formats *correctly* for
 * each, `en-IN` for INR gives lakhs/crores grouping, `en-AE` for AED, etc.
 * Falling back to `en-US` is safe for any currency Intl.NumberFormat supports.
 */
const LOCALE_FOR_CURRENCY = {
  INR: 'en-IN', USD: 'en-US', EUR: 'en-IE', GBP: 'en-GB',
  AED: 'en-AE', SAR: 'en-SA', QAR: 'en-QA', KWD: 'en-KW', OMR: 'en-OM', BHD: 'en-BH',
  SGD: 'en-SG', CAD: 'en-CA', AUD: 'en-AU', JPY: 'ja-JP', CHF: 'de-CH',
  HKD: 'en-HK', NZD: 'en-NZ', ZAR: 'en-ZA', THB: 'th-TH', MYR: 'ms-MY',
  // Phase 55 follow-up #5 — broadened to cover new currencies. Locales
  // are illustrative (Intl falls back to en-US gracefully when missing).
  IRR: 'fa-IR', IQD: 'ar-IQ', AFN: 'fa-AF',
  ISK: 'is-IS', RON: 'ro-RO', BGN: 'bg-BG', RSD: 'sr-RS', RUB: 'ru-RU', UAH: 'uk-UA',
  KZT: 'kk-KZ', UZS: 'uz-UZ',
  FJD: 'en-FJ',
  MAD: 'ar-MA', TND: 'ar-TN', GHS: 'en-GH', TZS: 'en-TZ', UGX: 'en-UG', ETB: 'en-ET', MUR: 'en-MU',
  CLP: 'es-CL', COP: 'es-CO', PEN: 'es-PE', UYU: 'es-UY',
  JMD: 'en-JM', TTD: 'en-TT',
};

/** Default decimals per currency. 0 for INR/JPY/KRW (no minor units in habit),
 *  2 for most, 3 for high-denomination Gulf currencies (KWD/BHD/OMR). */
const DEFAULT_DECIMALS = {
  JPY: 0, KRW: 0, IDR: 0, VND: 0, HUF: 0,
  KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3,
  // Phase 55 follow-up #5 — zero-decimal currencies in common practice.
  // CLP is technically 2-decimal per ISO but always displayed as integers
  // in Chile; IQD/IRR/AFN/UGX/TZS/UZS/RWF tickets are quoted as integers.
  CLP: 0, COP: 0, UGX: 0, TZS: 0, UZS: 0, IQD: 0, IRR: 0, AFN: 0, RWF: 0,
  // 3-decimal Gulf-style:
  // (KWD/BHD/OMR/JOD/TND already above)
  // Everything else: 2.
};

export function defaultDecimals(currency) {
  const c = (currency || 'INR').toUpperCase();
  if (DEFAULT_DECIMALS[c] !== undefined) return DEFAULT_DECIMALS[c];
  return 2;
}

/**
 * formatMoney, the workhorse.
 *
 * @param {number} amount
 * @param {string} currency          ISO 4217 (case-insensitive)
 * @param {object} [opts]
 * @param {number} [opts.decimals]   default per-currency
 * @param {boolean}[opts.compact]    enable lakh/crore for INR + k/M/B for others
 * @param {string} [opts.locale]     override locale
 * @returns {string}
 *
 * Edge cases:
 *   • amount === null/undefined → ' '
 *   • currency unknown to Intl → 'AMT 1,234.00' (3-letter code as a prefix)
 *   • negative → minus before symbol via {currencySign: 'accounting'} is too
 *     parenthesis-y; we manually move the minus sign.
 */
export function formatMoney(amount, currency = 'INR', opts = {}) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return ' ';
  const cur = (currency || 'INR').toUpperCase();
  const decimals = opts.decimals !== undefined ? opts.decimals : defaultDecimals(cur);
  const locale = opts.locale || LOCALE_FOR_CURRENCY[cur] || 'en-US';
  const neg = Number(amount) < 0;
  const abs = Math.abs(Number(amount));

  // Indian compaction: lakh / crore. Below 1L we fall through to the full
  // Intl format so "₹95,000" never becomes "₹0.95L".
  if (opts.compact && INDIAN_GROUPING.has(cur)) {
    if (abs >= 1e7) {
      const v = abs / 1e7;
      return `${neg ? '-' : ''}₹${v.toFixed(v >= 100 ? 0 : 2)} cr`;
    }
    if (abs >= 1e5) {
      const v = abs / 1e5;
      return `${neg ? '-' : ''}₹${v.toFixed(v >= 100 ? 0 : 2)} L`;
    }
    // fall through to full Intl format below
  }
  // Western compaction: k / M / B. Threshold 1e4 so $10,000 compacts but $999 stays full.
  if (opts.compact && !INDIAN_GROUPING.has(cur)) {
    if (abs >= 1e9) return `${neg ? '-' : ''}${currencySymbol(cur)}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${neg ? '-' : ''}${currencySymbol(cur)}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e4) return `${neg ? '-' : ''}${currencySymbol(cur)}${(abs / 1e3).toFixed(1)}k`;
  }

  let formatted;
  try {
    formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(abs);
  } catch (e) {
    // Unknown currency code, fall back to ISO-prefix format.
    formatted = `${cur} ${abs.toFixed(decimals)}`;
  }
  return neg ? `-${formatted}` : formatted;
}

/** Return just the currency symbol (₹, $, €, د.إ, etc.) for a code. */
export function currencySymbol(currency = 'INR') {
  try {
    const parts = new Intl.NumberFormat(LOCALE_FOR_CURRENCY[currency] || 'en-US', {
      style: 'currency', currency, currencyDisplay: 'symbol',
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency')?.value;
    return sym || currency;
  } catch {
    return currency;
  }
}

/**
 * The "headline parenthesis" pattern.
 *
 * `formatNativeWithBase(1200, 'AED', { rate: 22.66, base: 'INR' })`
 *   → "AED 1,200 · ≈ ₹27,192"
 *
 * If native === base, returns just the native formatted value (no noise).
 */
export function formatNativeWithBase(amount, nativeCurrency, { rate, base } = {}) {
  const native = formatMoney(amount, nativeCurrency);
  if (!rate || !base || nativeCurrency === base) return native;
  const conv = formatMoney(Number(amount) * Number(rate), base);
  return `${native} · ≈ ${conv}`;
}

/**
 * Build the "As of FX rates DD MMM YYYY · Source: …" disclosure line
 * that sits beneath every aggregate headline. Returns a plain string  
 * the caller is free to wrap it in <span> with their own styling.
 */
export function fxAsOfLine({ as_of_date, source, stale_days } = {}) {
  if (!as_of_date) return '';
  let d;
  try {
    d = new Date(`${as_of_date}T00:00:00Z`);
  } catch {
    d = new Date();
  }
  const dStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const srcLabel = friendlySource(source);
  const staleWarning = stale_days > 3 ? ` · ${stale_days}d old` : '';
  return `As of FX rates ${dStr} · Source: ${srcLabel}${staleWarning}`;
}

function friendlySource(s) {
  if (!s) return 'cached';
  if (s.includes('open_er_api')) return 'Exchange-Rate-API';
  if (s.includes('frankfurter')) return 'Frankfurter (ECB)';
  if (s === 'identity') return 'same currency';
  return s;
}

// =============================================================================
// API helpers, thin wrappers around /api/fx/*
// =============================================================================

/** GET /api/fx/rate, returns { from, to, rate, as_of_date, source, stale_days } */
export async function fetchFxRate(src, dst, on_date) {
  if (!src || !dst) return null;
  try {
    const params = { src, dst };
    if (on_date) params.on_date = on_date;
    const r = await axios.get(`${BACKEND_URL}/api/fx/rate`, { params, timeout: 8000 });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** POST /api/fx/convert, returns full conversion object */
export async function convertAmount(amount, src, dst, on_date) {
  try {
    const r = await axios.post(`${BACKEND_URL}/api/fx/convert`, { amount, src, dst, on_date }, { timeout: 8000 });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** GET /api/networth/breakdown, returns per-currency breakdown + base total */
export async function fetchNetWorthBreakdown(token) {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/networth/breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 10000,
    });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** GET /api/dashboard/breakdown, per-currency income/expense/surplus + base totals */
export async function fetchDashboardBreakdown(token, period = 'month') {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/dashboard/breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { period },
      timeout: 10000,
    });
    return r.data;
  } catch (e) {
    return null;
  }
}

// =============================================================================
// Phase 38.5, generic breakdown fetchers + shape adapter for FxBreakdownCard
// =============================================================================
// The new endpoints (`/api/projections/breakdown`, `/api/fi/breakdown`,
// `/api/goals/breakdown`) all return rows shaped:
//
//   { currency, native: {bucket: value, ...}, base: {bucket: value, ...} | null,
//     rate, as_of_date, source, stale_days }
//
// The existing <FxBreakdownCard /> expects FLAT rows with per-column
// `nativeKey` + `baseKey` (e.g. `native_net`, `base_net`). `flattenBreakdownRows`
// bridges the two so we don't have to fork the component.
// =============================================================================

/**
 * Flatten new-shape breakdown rows into the legacy flat shape the existing
 * <FxBreakdownCard /> expects.
 *
 * @param {Array}    rows    rows from a /api/<page>/breakdown endpoint
 * @param {string[]} buckets bucket names to flatten (e.g. ['net'] or ['target','current'])
 * @returns Array of rows w/ keys: `native_<bucket>`, `base_<bucket>`, rate, etc.
 */
export function flattenBreakdownRows(rows, buckets) {
  return (rows || []).map((row) => {
    const flat = {
      currency: row.currency,
      rate: row.rate,
      as_of_date: row.as_of_date,
      source: row.source,
      stale_days: row.stale_days,
    };
    for (const b of buckets) {
      flat[`native_${b}`] = row.native?.[b];
      flat[`base_${b}`] = row.base?.[b] ?? null;
    }
    return flat;
  });
}

/** GET /api/projections/breakdown, corpus across native currencies. */
export async function fetchProjectionsBreakdown(token) {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/projections/breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 10000,
    });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** GET /api/fi/breakdown, corpus across native currencies (FI context). */
export async function fetchFiBreakdown(token) {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/fi/breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 10000,
    });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** GET /api/goals/breakdown, goal targets/current/remaining per currency. */
export async function fetchGoalsBreakdown(token) {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/goals/breakdown`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 10000,
    });
    return r.data;
  } catch (e) {
    return null;
  }
}

/** PATCH /api/profile/base-currency, change user's headline currency. */
export async function setBaseCurrency(token, baseCurrency) {
  if (!token) return { ok: false, error: 'Sign in first.' };
  try {
    const r = await axios.patch(
      `${BACKEND_URL}/api/profile/base-currency`,
      { base_currency: baseCurrency },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
    );
    return r.data;
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || e?.message };
  }
}
